import React from 'react';
import { Field, getIn, FormikActions, FormikState } from 'formik';

import { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import { PASCProps } from 'buyside/components/presentation/UserApplication/Pages/proApplicationSubmission/ProApplicationSubmissionContainer';
import { WizardSubmitButton } from 'buyside/components/presentation/Form/Wizard/Wizard';
import Button from 'buyside/components/presentation/Button/button';
import StripeFailure from 'buyside/components/presentation/Stripe/StripeFailure';
import useMakeStripeCheckout from 'buyside/components/presentation/Stripe/useMakeStripeCheckout';
import {
  buysideDisclaimer,
  allocatorDisclaimer,
  retailDisclaimer
} from '../../Helpers/userApplicationPageConstants';
import PageHeader from '../../Helpers/pageHeader';
import PageFooter from '../../Helpers/PageFooter';
import RecaptchaFailed from '../../Helpers/RecaptchaFailed';

import { termsOfUseUrl, privacyPolicyUrl } from 'buyside/util/urlHelper';
const {
  retailTrialPeriodDays,
  retailTrialExtensionPeriodDays
} = require('buyside/util/constantsFromRails.json');

import {
  InvestorTypes,
  ApplicantTypes,
  ApplicationFormValues
} from 'interfaces/userApplication/userApplication';
import { WizardContextProps } from 'interfaces/wizard/wizard';
import { STRIPE_PLANS } from 'interfaces/payment/plans';

import styles from '../pageStyles.scss';

export type PASProps = Pick<FormikState<ApplicationFormValues>, 'errors' | 'touched' | 'values'> &
  PASCProps & {
    setTouched: FormikActions<ApplicationFormValues>['setTouched'];
    setSubmitting: FormikActions<ApplicationFormValues>['setSubmitting'];
    next: WizardContextProps['next'];
  };

const ProApplicationSubmission: React.FunctionComponent<PASProps> = ({
  email,
  errors,
  handleSubmit,
  isLoading,
  isRobot,
  setTouched,
  setSubmitting,
  next,
  touched,
  values
}): React.ReactElement => {
  const { onSubmit, loading, error: stripeCheckoutError } = useMakeStripeCheckout(
    STRIPE_PLANS.retailTier1
  );

  const { investorType, applicantType } = values;
  const isBasic = applicantType === ApplicantTypes.basic;
  let disclaimer, submitButtonText: string;
  if (isBasic) {
    disclaimer = retailDisclaimer;
    submitButtonText = 'Sign Up Today';
  } else {
    disclaimer = investorType === InvestorTypes.allocator ? allocatorDisclaimer : buysideDisclaimer;
    submitButtonText = 'Submit Application';
  }

  const isTouched = getIn(touched, 'acceptedDisclaimer');
  const error = getIn(errors, 'acceptedDisclaimer');
  const isInputChecked = getIn(values, 'acceptedDisclaimer');

  return (
    <>
      <PageHeader subHeader={isBasic ? undefined : 'Submit your application'} />
      {isBasic && (
        <div className={styles.basicDescription}>
          <p>
            You will be enrolled in a free {retailTrialPeriodDays}-day trial upon registration.
            Purchase a membership today by completing your billing information, and we will extend
            your free trial to {retailTrialExtensionPeriodDays} days.
          </p>
          <p>
            While you won't be charged for your free trial, you will be upgraded to a paid
            membership plan automatically at the end of the {retailTrialExtensionPeriodDays}-day
            trial period. You can turn off your membership renewal anytime by going to your account
            settings.
          </p>
        </div>
      )}
      <div className={styles.inputFormColumn}>
        <label className={styles.checkboxes} data-heap="application-terms-conditions">
          <Field type="checkbox" name="acceptedDisclaimer" disabled={isLoading} />
          {disclaimer}
        </label>
        {isTouched && error && <div className={styles.fieldError}>{error}</div>}
      </div>
      {isBasic ? (
        <>
          <div className={styles.basicButtonContainer}>
            <div className={styles.individualButtonContainer}>
              <Button
                text={submitButtonText}
                type={BUTTON_TYPES.PRIMARY}
                buttonType={BUTTON_TYPES.BUTTON}
                onClick={async (): Promise<void> => {
                  await handleSubmit(
                    {
                      ...values,
                      isStripeRegistered: true,
                      acceptedDisclaimer: isInputChecked
                    },
                    setTouched,
                    setSubmitting,
                    next
                  );
                  onSubmit(email);
                }}
                disabled={!isInputChecked}
                isLoading={loading}
              />
              <span className={styles.buttonSubText}>Begin 10-day Trial</span>
            </div>
            <div className={styles.individualButtonContainer}>
              <WizardSubmitButton
                analyticsTag="submit_application"
                disabled={!isInputChecked}
                isLoading={isLoading}
                submitText="Skip Payment Method"
                isSecondary
              />
              <span className={styles.buttonSubText}>Begin 5-day Trial</span>
            </div>
          </div>
          {stripeCheckoutError && <StripeFailure plan={STRIPE_PLANS.retailTier1} />}
        </>
      ) : (
        <WizardSubmitButton
          analyticsTag="submit_application"
          isLoading={isLoading}
          submitText={submitButtonText}
        />
      )}
      {isRobot && <RecaptchaFailed />}
      <div className={styles.footerText}>
        <p>
          By clicking "{submitButtonText}"{isBasic ? ' or "Skip Payment Method"' : ''}, I agree to
          SumZero's{' '}
          <a href={termsOfUseUrl()} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>{' '}
          and{' '}
          <a href={privacyPolicyUrl()} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      </div>
      <PageFooter />
    </>
  );
};

export default ProApplicationSubmission;
